import React, { useState } from 'react';
import { Users, Star, Building2, Plus, MoreHorizontal, UserX, X, ChevronLeft } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@vaiu/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@vaiu/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Separator } from '@vaiu/ui/separator';
import { Contact } from '@/services/platformApi';

interface ContactGroupsProps {
  isOpen: boolean;
  onClose: () => void;
  contacts: Contact[];
}

const ContactGroups: React.FC<ContactGroupsProps> = ({
  isOpen,
  onClose,
  contacts
}) => {
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const groupedContacts = {
    frequent: contacts.filter(c => c.relationship === 'frequent'),
    recent: contacts.filter(c => c.relationship === 'recent'),
    business: contacts.filter(c => c.relationship === 'business'),
    infrequent: contacts.filter(c => c.relationship === 'infrequent'),
  };

  const relationshipConfig = {
    frequent: {
      title: 'Frequent Contacts',
      description: 'People you transact with regularly',
      icon: Star,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    recent: {
      title: 'Recent Contacts',
      description: 'People you\'ve transacted with recently',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    business: {
      title: 'Business Contacts',
      description: 'Merchants and business accounts',
      icon: Building2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    infrequent: {
      title: 'Other Contacts',
      description: 'All other contacts',
      icon: Users,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50'
    }
  };

  const toggleContactSelection = (contactId: string) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const bulkOperations = [
    {
      label: 'Send Message to All',
      action: () => {
        console.log('Send message to', selectedContacts);
        // Implementation would go here
      }
    },
    {
      label: 'Export Selected',
      action: () => {
        console.log('Export contacts', selectedContacts);
        // Implementation would go here
      }
    },
    {
      label: 'Remove Selected',
      action: () => {
        console.log('Remove contacts', selectedContacts);
        // Implementation would go here
      },
      destructive: true
    }
  ];

  const renderContactGroup = (
    groupKey: keyof typeof groupedContacts,
    groupContacts: Contact[]
  ) => {
    const config = relationshipConfig[groupKey];
    const Icon = config.icon;

    if (groupContacts.length === 0) return null;

    return (
      <Card key={groupKey}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`p-2 rounded-full ${config.bgColor}`}>
                <Icon className={`w-4 h-4 ${config.color}`} />
              </div>
              <div>
                <h3 className="font-medium">{config.title}</h3>
                <p className="text-sm text-muted-foreground font-normal">
                  {config.description}
                </p>
              </div>
            </div>
            <Badge variant="secondary">
              {groupContacts.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {groupContacts.map((contact) => (
              <div
                key={contact.id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedContacts.includes(contact.id)
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => toggleContactSelection(contact.id)}
              >
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={`https://avatar.vercel.sh/${contact.email || contact.name}`} />
                    <AvatarFallback className="bg-primary/10 text-primary font-medium text-sm">
                      {getInitials(contact.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{contact.name}</h4>
                    <p className="text-sm text-muted-foreground truncate">
                      {contact.email || contact.phoneNumber || 'No contact info'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {contact.totalTransactions} transactions
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Don't render anything if not open
  if (!isOpen) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Contact Groups</h2>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Organize and manage your contacts by groups
      </p>

      <Tabs defaultValue="groups" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="groups">View by Groups</TabsTrigger>
          <TabsTrigger value="manage">Bulk Operations</TabsTrigger>
        </TabsList>

        <TabsContent value="groups" className="space-y-6 mt-6">
          <div className="space-y-4">
            {Object.entries(groupedContacts).map(([key, groupContacts]) =>
              renderContactGroup(key as keyof typeof groupedContacts, groupContacts)
            )}
          </div>
        </TabsContent>

        <TabsContent value="manage" className="space-y-6 mt-6">
          {/* Selection Header */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <h3 className="font-medium">Bulk Operations</h3>
              <p className="text-sm text-muted-foreground">
                {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
              </p>
            </div>
            
            {selectedContacts.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <MoreHorizontal className="w-4 h-4 mr-2" />
                    Actions ({selectedContacts.length})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {bulkOperations.map((operation, index) => (
                    <React.Fragment key={operation.label}>
                      <DropdownMenuItem
                        onClick={operation.action}
                        className={operation.destructive ? 'text-destructive focus:text-destructive' : ''}
                      >
                        {operation.label}
                      </DropdownMenuItem>
                      {index < bulkOperations.length - 1 && operation.destructive && (
                        <DropdownMenuSeparator />
                      )}
                    </React.Fragment>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Contact Selection Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedContacts.includes(contact.id)
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => toggleContactSelection(contact.id)}
              >
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={`https://avatar.vercel.sh/${contact.email || contact.name}`} />
                    <AvatarFallback className="bg-primary/10 text-primary font-medium text-sm">
                      {getInitials(contact.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{contact.name}</h4>
                    <p className="text-sm text-muted-foreground truncate">
                      {contact.email || contact.phoneNumber || 'No contact info'}
                    </p>
                    <Badge variant="outline" className="text-xs mt-1">
                      {relationshipConfig[contact.relationship].title}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {contacts.length === 0 && (
            <div className="text-center py-8">
              <UserX className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No contacts found</h3>
              <p className="text-muted-foreground">
                Add some contacts to use bulk operations
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Separator />

      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default ContactGroups; 